import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Collapse,
  Divider,
  Stack,
  alpha,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { useVoyagerStore } from '../../stores/useVoyagerStore';
import { hasConditions, serializeFilterGroup } from '../../stores/helpers/filterHelpers';

/**
 * Builds a markdown-formatted diagnostic block for the user to share with the
 * Voyager team. Captures the full workspace context that produced the
 * failed query so triage doesn't require a back-and-forth.
 */
function buildDiagnosticReport(args: {
  errorMessage: string;
  errorDetails: ReturnType<typeof useVoyagerStore.getState>['errorDetails'];
  state: ReturnType<typeof useVoyagerStore.getState>;
}): string {
  const { errorMessage, errorDetails, state } = args;

  const filterGroupSerialized = hasConditions(state.filterGroup)
    ? JSON.stringify(serializeFilterGroup(state.filterGroup), null, 2)
    : '(none)';

  const metricsBlock = state.selectedMetrics.length
    ? state.selectedMetrics
        .map((m) => `  - ${m.metricName}${m.id ? ` (instance: ${m.id})` : ''}`)
        .join('\n')
    : '  (none)';

  const dimensionsBlock = state.selectedDimensions.length
    ? state.selectedDimensions.map((d) => `  - ${d}`).join('\n')
    : '  (none)';

  const orderByBlock = state.orderBy.length
    ? state.orderBy.map((o) => `  - ${o.column} ${o.direction}`).join('\n')
    : '  (none)';

  const calcAttrsBlock = state.calculatedAttributes.length
    ? state.calculatedAttributes
        .map((c) => `  - ${c.name} = ${c.formula}${c.type ? ` [${c.type}]` : ''}`)
        .join('\n')
    : '  (none)';

  const lines = [
    '# Stroma — Query Failure Report',
    '',
    `**Generated:** ${new Date().toISOString()}`,
    `**App URL:** ${window.location.href}`,
    '',
    '## Error',
    '',
    `- **Code:** ${errorDetails?.errorCode ?? '(unknown)'}`,
    `- **Title:** ${errorDetails?.title ?? '(none)'}`,
    `- **SQLSTATE:** ${errorDetails?.sqlstate ?? '(n/a)'}`,
    errorDetails?.missingObject ? `- **Missing object:** \`${errorDetails.missingObject}\`` : null,
    errorDetails?.errorPosition !== undefined
      ? `- **Position:** ${errorDetails.errorPosition}`
      : null,
    '',
    `**Message:** ${errorDetails?.message ?? errorMessage}`,
    errorDetails?.hint ? `\n**Hint:** ${errorDetails.hint}` : null,
    '',
    '### Raw database error',
    '```',
    errorDetails?.rawError ?? errorMessage ?? '(none)',
    '```',
    '',
    '## Workspace context',
    '',
    `- **Domain:** ${state.selectedDomain ?? '(none)'}`,
    `- **Perspective:** ${state.selectedPerspective ?? '(none)'}`,
    `- **Active draft:** ${state.activeDraftId ?? '(none)'}`,
    `- **Model source:** ${state.userSettings?.modelSource ?? '(default)'}`,
    `- **Result limit:** ${state.userSettings?.resultLimit ?? '(default)'}`,
    `- **Date field:** ${state.activeDateField ?? '(none)'}`,
    `- **Date range preset:** ${state.dateRangePreset ?? '(none)'}`,
    `- **Date range:** ${state.dateRangeStart ?? '(start?)'} → ${state.dateRangeEnd ?? '(end?)'}`,
    '',
    '### Metrics',
    metricsBlock,
    '',
    '### Dimensions',
    dimensionsBlock,
    '',
    '### Calculated attributes',
    calcAttrsBlock,
    '',
    '### Order by',
    orderByBlock,
    '',
    '### Filters',
    '```json',
    filterGroupSerialized,
    '```',
    '',
    '## Generated SQL',
    '```sql',
    state.querySQL || '(SQL not available — failure may have occurred before generation)',
    '```',
    '',
    '## Environment',
    `- **User agent:** ${navigator.userAgent}`,
  ].filter((line): line is string => line !== null);

  return lines.join('\n');
}

const ResultErrorCard: React.FC = () => {
  const error = useVoyagerStore((s) => s.error);
  const errorDetails = useVoyagerStore((s) => s.errorDetails);
  const [techExpanded, setTechExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!error) return null;

  const handleCopy = async () => {
    const state = useVoyagerStore.getState();
    const report = buildDiagnosticReport({
      errorMessage: error,
      errorDetails,
      state,
    });
    try {
      await navigator.clipboard.writeText(report);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = report;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      } finally {
        document.body.removeChild(textarea);
      }
    }
  };

  const title = errorDetails?.title || 'Query failed';
  const message = errorDetails?.message || error;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
      <Paper
        elevation={0}
        sx={(theme) => ({
          width: '100%',
          maxWidth: 760,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
          borderLeft: `4px solid ${theme.palette.error.main}`,
          bgcolor: theme.palette.background.paper,
          overflow: 'hidden',
        })}
      >
        {/* ── Header ─────────────────────────────────────────────────── */}
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            p: 2.5,
            bgcolor: alpha(theme.palette.error.main, 0.04),
          })}
        >
          {/* Icon anchor */}
          <Box
            sx={(theme) => ({
              flexShrink: 0,
              width: 44,
              height: 44,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.error.main, 0.12),
              color: theme.palette.error.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            })}
          >
            <ErrorOutlineIcon sx={{ fontSize: 26 }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 0.75, rowGap: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={700} color="error.dark" sx={{ lineHeight: 1.3 }}>
                {title}
              </Typography>
              {errorDetails?.errorCode && (
                <Chip
                  label={errorDetails.errorCode}
                  size="small"
                  color="error"
                  sx={{
                    height: 22,
                    fontSize: '0.7rem',
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    letterSpacing: 0.3,
                  }}
                />
              )}
              {errorDetails?.sqlstate && (
                <Chip
                  label={`SQLSTATE ${errorDetails.sqlstate}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 22,
                    fontSize: '0.7rem',
                    fontFamily: 'monospace',
                    color: 'text.secondary',
                    borderColor: 'divider',
                  }}
                />
              )}
            </Stack>

            <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.55 }}>
              {message}
            </Typography>
          </Box>
        </Box>

        {/* ── Hint callout ───────────────────────────────────────────── */}
        {errorDetails?.hint && (
          <>
            <Divider sx={{ borderColor: (t) => alpha(t.palette.error.main, 0.12) }} />
            <Box
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                px: 2.5,
                py: 1.5,
                bgcolor: alpha(theme.palette.warning.main, 0.06),
              })}
            >
              <LightbulbOutlinedIcon sx={{ fontSize: 18, color: 'warning.dark', mt: 0.25 }} />
              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
                {errorDetails.hint}
              </Typography>
            </Box>
          </>
        )}

        {/* ── Action row ─────────────────────────────────────────────── */}
        <Divider sx={{ borderColor: (t) => alpha(t.palette.error.main, 0.12) }} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2.5,
            py: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Button
            size="small"
            variant="contained"
            color={copied ? 'success' : 'error'}
            disableElevation
            startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
            onClick={handleCopy}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {copied ? 'Copied — paste to share with Stroma' : 'Copy diagnostic report'}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="text"
            onClick={() => setTechExpanded((v) => !v)}
            endIcon={techExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            {techExpanded ? 'Hide technical details' : 'Show technical details'}
          </Button>
        </Box>

        {/* ── Tech details panel ─────────────────────────────────────── */}
        <Collapse in={techExpanded} unmountOnExit>
          <Divider sx={{ borderColor: (t) => alpha(t.palette.error.main, 0.12) }} />
          <Box sx={{ p: 2.5, pt: 2 }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                fontWeight: 600,
                mb: 1,
              }}
            >
              Raw database error
            </Typography>
            <Box
              sx={{
                p: 1.5,
                bgcolor: 'grey.900',
                color: 'grey.100',
                fontFamily: 'monospace',
                fontSize: '0.78rem',
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'grey.800',
              }}
            >
              {errorDetails?.rawError || error}
            </Box>

            {(errorDetails?.missingObject || errorDetails?.errorPosition !== undefined) && (
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5, rowGap: 0.5 }}>
                {errorDetails?.missingObject && (
                  <Chip
                    label={`Missing: ${errorDetails.missingObject}`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.7rem', fontFamily: 'monospace' }}
                  />
                )}
                {errorDetails?.errorPosition !== undefined && (
                  <Chip
                    label={`Position ${errorDetails.errorPosition}`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.7rem', fontFamily: 'monospace' }}
                  />
                )}
              </Stack>
            )}
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default ResultErrorCard;
